import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { updateEventDetailsById } from '../../../../../../app/store/slices/event/event';
import { errorToast, successToastTopRight } from '../../../../../../utils/toasts/toasts';

// TODO: Implement commission paid by end user and selected bank account key field in get event api
export default function useBankInfo() {
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const { eventData } = useSelector((state) => state.event);
    const { bankDetails } = useSelector((state) => state.bank);

    const initialValues = {
        bankName: '',
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        accountType: '',
        nickName: '',
        commissionPayableByEndUser: eventData?.commission_paid_by_end_user || false,
        // selectedBankAccount: eventData?.bank_detail_id || null,
        selectedBankAccount:
            bankDetails?.data?.find((account) => account.id === eventData?.bank_detail_id) || null,
        isEditable: false,
        isCommissionEditable: false
    };

    const validationSchema = Yup.object({
        bankName: Yup.string(),
        accountHolderName: Yup.string(),
        accountNumber: Yup.string(),
        ifscCode: Yup.string(),
        accountType: Yup.string(),
        nickName: Yup.string(),
        commissionPayableByEndUser: Yup.boolean(),
        isEditable: Yup.boolean(),
        isCommissionEditable: Yup.boolean()
    });

    // console.log("Initial Values:", initialValues);

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                setLoading(true);

                const payload = {
                    commission_paid_by_end_user: values.commissionPayableByEndUser,
                    bank_detail_id: values.selectedBankAccount?.id || null
                };

                // console.log("Payload for updating bank details:", payload);

                const res = await dispatch(
                    updateEventDetailsById({ eventId: eventData?.eventId, eventData: payload })
                ).unwrap();
                
                successToastTopRight(res?.message || 'Bank details updated successfully.');

                if(formik.values.isCommissionEditable) {
                    formik.setFieldValue('isCommissionEditable', false);
                }

                if(formik.values.isEditable) {
                    formik.setFieldValue('isEditable', false);
                }
            } catch (error) {
                setLoading(false);
                // console.error("Error updating bank details:", error);
                errorToast(error?.message || 'Failed to update bank details. Please try again.');
            } finally {
                setLoading(false);
            }
        }
    });

    function handleBankAccountSelection(event, newValue) {
        formik.setFieldValue('selectedBankAccount', newValue);
        formik.setFieldValue('bankName', newValue?.bank_name || '');
        formik.setFieldValue('accountHolderName', newValue?.account_holder_name || '');
        formik.setFieldValue('accountNumber', newValue?.account_number || '');
        formik.setFieldValue('ifscCode', newValue?.ifsc_code || '');
        formik.setFieldValue('accountType', newValue?.account_type || '');
        formik.setFieldValue('nickName', newValue?.nickname || '');
    }

    useEffect(() => {
        if (eventData?.bank_detail_id || formik.values.selectedBankAccount) {
            const selectedBankAccount = bankDetails?.data?.find(
                (account) => account.id === eventData?.bank_detail_id
            );
            if (selectedBankAccount) {
                formik.setFieldValue('selectedBankAccount', selectedBankAccount);
                formik.setFieldValue('bankName', selectedBankAccount.bank_name || '');
                formik.setFieldValue(
                    'accountHolderName',
                    selectedBankAccount.account_holder_name || ''
                );
                formik.setFieldValue('accountNumber', selectedBankAccount.account_number || '');
                formik.setFieldValue('ifscCode', selectedBankAccount.ifsc_code || '');
                formik.setFieldValue('accountType', selectedBankAccount.account_type || '');
                formik.setFieldValue('nickName', selectedBankAccount.nickname || '');
            }
        }
    }, [eventData?.bank_detail_id]);

    const isCommissionPaidByEndUserProvided = useMemo(() => {
        return eventData?.commission_paid_by_end_user !== null ? true : false;
    }, [eventData?.commission_paid_by_end_user]);

    return {
        formik,
        handleBankAccountSelection,
        loading,
        isCommissionPaidByEndUserProvided
    };
}